import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Plus, Search, AlertCircle, Users } from 'lucide-react'
import { isAxiosError } from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import { humaniseAction } from './adminFormat'
import { createAdminUser, updateAdminUser } from '../../services/api'
import { useAdminUsers, type AdminUser } from '../../hooks/useAuthedFetch'
import { useTimedOut } from '../../hooks/useTimedOut'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { shortDate } from '../../lib/shortDate'

const ADMIN_ROLE = 'innovateflow-admin'
const ROLE_LABELS: Record<string, string> = {
  inventor: 'Inventor',
  collaborator: 'Collaborator',
  [ADMIN_ROLE]: 'Admin',
}
const roleLabel = (role: string) => ROLE_LABELS[role] ?? role

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    return data?.error || data?.message || err.message || fallback
  }
  if (err instanceof Error) return err.message
  return fallback
}

export default function UserManagement() {
  const [showCreate, setShowCreate] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState('inventor')
  const [searchTerm, setSearchTerm] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [disableTarget, setDisableTarget] = useState<AdminUser | null>(null)
  // UX-046: a role change that grants or removes platform-admin power is held
  // here until the admin confirms it; nothing is sent on the select change.
  const [roleChange, setRoleChange] = useState<{ user: AdminUser; role: string } | null>(null)
  const { user: me } = useAuth()

  // BUG-147: Cognito paginates ListUsers — useAdminUsers follows the token
  // until the directory is exhausted, then we filter over the full set. Uses
  // the direct-fetch path (see hooks/useAuthedFetch) because the axios read
  // hangs inside the /admin route context.
  const { data, isLoading, isError, refetch } = useAdminUsers()

  const createMutation = useMutation({
    mutationFn: () => createAdminUser({ email: newEmail, role: newRole }),
    onSuccess: () => {
      refetch()
      setShowCreate(false)
      setNewEmail('')
      setNewRole('inventor')
      setCreateError(null)
    },
    // BUG-121: surface create failures in the modal instead of failing silently.
    onError: (err) => setCreateError(errorMessage(err, 'Failed to create user. Please try again.')),
  })

  // Lambda updateAdminUser takes { role?, enabled? }
  const updateMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: { role?: string; enabled?: boolean } }) =>
      updateAdminUser(userId, updates),
    onSuccess: () => {
      refetch()
      setActionError(null)
    },
    // BUG-121: role/enable-disable changes now report failures to the admin.
    onError: (err) => setActionError(errorMessage(err, 'Failed to update the user. Please try again.')),
  })

  const isSelf = (u: AdminUser) =>
    !!me && (u.user_id === me.id || u.email.toLowerCase() === (me.email ?? '').toLowerCase())

  const requestRoleChange = (user: AdminUser, role: string) => {
    if (role === user.role) return
    const touchesAdmin = role === ADMIN_ROLE || user.role === ADMIN_ROLE
    // Platform-admin power comes from the innovateflow-admin GROUP as well as the
    // role attribute, so a row can read "Inventor" for someone who is an admin.
    // Judge "is this me, and am I an admin" by both, or the guard misses the
    // exact person it exists to protect.
    const meIsAdmin = !!me?.groups?.includes(ADMIN_ROLE) || me?.role === ADMIN_ROLE
    if (isSelf(user) && role !== ADMIN_ROLE && (user.role === ADMIN_ROLE || meIsAdmin)) {
      // The select is controlled, so it stays on Admin; say why.
      setActionError("You can't remove your own admin access. Ask another administrator to change your role.")
      return
    }
    if (touchesAdmin) {
      setActionError(null)
      setRoleChange({ user, role })
      return
    }
    updateMutation.mutate({ userId: user.user_id, updates: { role } })
  }

  const users = data?.users || []
  const filteredUsers = users.filter(
    (u) => u.email.toLowerCase().includes(searchTerm.toLowerCase()) || u.role.includes(searchTerm.toLowerCase())
  )

  // Bound the skeleton so a slow/stuck read never walls the page (Fig33 defect).
  const loadTimedOut = useTimedOut(isLoading)
  if (isLoading && !loadTimedOut) return <LoadingSkeleton rows={5} type="table" />

  return (
    <div>
      <PageHeader
        title="Platform Users"
        description="Every user on the platform. Search, inspect and manage accounts — InnovateFlow administrators only."
        helpKey="admin-users"
        status={{ label: `${users.length} user${users.length === 1 ? '' : 's'}`, tone: 'neutral' }}
        primaryAction={
          <button
            onClick={() => { setCreateError(null); setShowCreate(true) }}
            className="flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800"
          >
            <Plus size={16} /> Create User
          </button>
        }
      />

      {isError && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-red-500/10 border border-red-200 px-4 py-3 text-sm text-red-700">
          <span className="flex items-center gap-2">
            <AlertCircle size={16} className="flex-shrink-0" />
            Couldn't load the user directory.
          </span>
          <button onClick={refetch} className="font-medium text-red-700 hover:text-red-800 underline">Retry</button>
        </div>
      )}

      {/* BUG-121: inline update (role / enable-disable) error banner */}
      {actionError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      <div className="mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users..."
            className="w-full pl-9 pr-3 py-2 border border-sky-200 rounded-lg text-sm focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none bg-white/80"
          />
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <EmptyState
          icon={Users}
          title={users.length === 0 ? 'No users yet' : 'No matching users'}
          description={
            users.length === 0
              ? 'Create the first platform user to get started.'
              : 'No users match your search. Try a different email or role.'
          }
          action={
            users.length === 0 ? (
              <button
                onClick={() => { setCreateError(null); setShowCreate(true) }}
                className="flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800"
              >
                <Plus size={16} /> Create User
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="light-card overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-sky-500/5 border-b border-sky-200/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-200/30">
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-white/30">
                  <td className="px-4 py-3 text-sm text-gray-800">{user.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={user.role}
                      aria-label={`Role for ${user.email}`}
                      onChange={(e) => requestRoleChange(user, e.target.value)}
                      className="text-xs text-gray-800 border border-sky-200 rounded px-2 py-1 focus:ring-1 focus:ring-iflow-500 outline-none bg-white"
                    >
                      {/* A role outside the three below (the directory holds plain "user"
                          accounts) used to render as "Inventor", a value nobody set. Show
                          what is really stored. */}
                      {!(user.role in ROLE_LABELS) && (
                        <option className="bg-white text-gray-800" value={user.role}>{humaniseAction(user.role)}</option>
                      )}
                      <option className="bg-white text-gray-800" value="inventor">Inventor</option>
                      <option className="bg-white text-gray-800" value="collaborator">Collaborator</option>
                      <option className="bg-white text-gray-800" value="innovateflow-admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      user.enabled ? 'bg-green-500/20 text-green-700' : 'bg-gray-500/20 text-gray-600'
                    }`}>
                      {user.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {user.created ? shortDate(user.created) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* Disabling an account is consequential → confirm it (P7).
                        Enabling is safe/reversible, so it stays a one-click action. */}
                    <button
                      onClick={() =>
                        user.enabled
                          ? setDisableTarget(user)
                          : updateMutation.mutate({ userId: user.user_id, updates: { enabled: true } })
                      }
                      className={`text-xs font-medium ${
                        user.enabled ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'
                      }`}
                    >
                      {user.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal — light theme (P: app theme, never dark) */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreate(false)} />
          <div className="relative bg-white rounded-xl border border-sky-200 shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-iflow-900 mb-4">Create User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="w-full px-3 py-2 border border-sky-200 rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-3 py-2 border border-sky-200 rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none bg-white"
                >
                  <option value="inventor">Inventor</option>
                  <option value="collaborator">Collaborator</option>
                  <option value="innovateflow-admin">Admin</option>
                </select>
              </div>
              {/* BUG-121: create failure feedback inside the modal */}
              {createError && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                  <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{createError}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100">Cancel</button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!newEmail.trim() || createMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin role change confirmation — granting or removing platform-admin
          power is consequential (UX-046). The real mutation runs only on confirm. */}
      <ConfirmDialog
        open={!!roleChange}
        title={roleChange?.role === ADMIN_ROLE ? 'Make this user an admin?' : 'Remove admin access?'}
        tone="danger"
        confirmLabel={roleChange?.role === ADMIN_ROLE ? 'Make admin' : 'Remove admin access'}
        message={
          roleChange
            ? roleChange.role === ADMIN_ROLE
              ? `${roleChange.user.email} will be able to see and manage every user, organisation and audit record on the platform. You can change this later.`
              : `${roleChange.user.email} will lose platform-admin access and become ${roleLabel(roleChange.role)}.`
            : ''
        }
        onConfirm={async () => {
          if (!roleChange) return
          await updateAdminUser(roleChange.user.user_id, { role: roleChange.role })
          refetch()
          setActionError(null)
        }}
        onClose={() => setRoleChange(null)}
      />

      {/* Disable account confirmation — real mutation, honest error, no fake success */}
      <ConfirmDialog
        open={!!disableTarget}
        title="Disable this account?"
        tone="danger"
        confirmLabel="Disable account"
        message={
          disableTarget
            ? `${disableTarget.email} will be signed out and blocked from signing in until you re-enable them. You can re-enable the account at any time.`
            : ''
        }
        onConfirm={async () => {
          if (!disableTarget) return
          await updateAdminUser(disableTarget.user_id, { enabled: false })
          refetch()
          setActionError(null)
        }}
        onClose={() => setDisableTarget(null)}
      />
    </div>
  )
}
